import React, { useState, useCallback } from 'react';
import { Download, AlertCircle, Loader2 } from 'lucide-react';

const BASE_URL = 'https://jasperd.ca';
const POLL_INTERVAL = 5000;
const MAX_RETRIES = 12; // 1 minute maximum polling time

const DownloadPacingExcelButton = ({ pacingData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const pollTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/task-status/${taskId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check task status');
      }

      return data;
    } catch (err) {
      throw new Error('Error checking task status');
    }
  }, []);

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setRetryCount(0);

    try {
      // Initial request to generate Excel
      const response = await fetch(`${BASE_URL}/api/download-pacing-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pacingData),
      });

      if (!response.ok) {
        throw new Error('Failed to initiate Excel generation');
      }

      const { task_id } = await response.json();

      // Start polling
      const pollInterval = setInterval(async () => {
        try {
          const status = await pollTaskStatus(task_id);

          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setIsLoading(false);
            window.location.href = `${BASE_URL}/api/download-task-result/${task_id}`;
          } else if (status.status === 'error') {
            clearInterval(pollInterval);
            setIsLoading(false);
            setError(status.error || 'Task failed');
          } else {
            setRetryCount(prev => {
              if (prev >= MAX_RETRIES) {
                clearInterval(pollInterval);
                setIsLoading(false);
                setError('Request timed out');
                return prev;
              }
              return prev + 1;
            });
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsLoading(false);
          setError(err.message);
        }
      }, POLL_INTERVAL);

      // Cleanup interval on component unmount
      return () => clearInterval(pollInterval);
    } catch (err) {
      setIsLoading(false);
      setError(err.message);
    }
  }, [pacingData, pollTaskStatus]);

  return (
    <div className="space-y-3">
      <button
        onClick={handleDownload}
        disabled={isLoading}
        className={`
          inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
          ${isLoading 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300 shadow-sm'}
          font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        `}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <Download className="w-3.5 h-3.5" />
            <span>Download Excel</span>
          </>
        )}
      </button>

      {error && (
        <div className="flex items-start gap-2 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isLoading && (
        <div className="text-xs text-gray-500">
          Generating your Excel file...
        </div>
      )}
    </div>
  );
};

export default DownloadPacingExcelButton;